import { Component, inject, Input } from '@angular/core';
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PostGetLocationSearchRequest } from '../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { LocationCoordinates } from '../../../shared/model/LocationCoordinates';
import { MCAppGatewayService } from '../../../shared/services/mcappgateway/mcappgateway.service';
import { CommonModule } from '@angular/common';
import { SearchStateService } from '../../shared/services/search-state.service';
import { MonitoringService } from '../../../shared/services/monitoring/monitoring-service';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { LocationSearchFilters } from '../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-location-search',
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    MatButtonModule, 
    MatIconModule, 
    MatFormFieldModule, 
    MatInputModule, 
    MatAutocompleteModule,
    MatProgressSpinner
  ],
  templateUrl: './location-search.component.html',
  styleUrl: './location-search.component.css'
})
export class LocationSearchComponent {
  mcAppGateway = inject(MCAppGatewayService);
  searchState = inject(SearchStateService);

  public title: string = 'Find a Location';
  public searchByText: string = 'Search by Facility or Location Name';
  public namePlaceholderText: string = 'Facility or Location Name';
  public cityZipPlaceholderText: string = 'City or zip code (required)*'
  public locationInformationRequiredText: string = 'Location information required*';
  public searchBtnText: string = 'Search';
  public quickSearchTitleText: string = 'Quick searches:';
  public showSpinner: boolean = false;


  @Input() locale: string = '';
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() locationQuickSearchOptions: any[] | null = null;
  @Input() locationSearchFilters: LocationSearchFilters | null = null;
  @Input() cityTypeAheadOptions: string[] = [];
  @Input() locationSearchTypeAheadOptions: string[] = [];

  locationSearchQuery$ = this.searchState.locationSearchQuery$;
  showSpinner$ = this.searchState.showSpinner$;
  filteredCityOptions$: Observable<string[]> = new Observable<string[]>();
  filteredLocationNames$: Observable<string[]> = new Observable<string[]>();

  sortMethod: "distance" | "availability" | "name" = "distance";
  locationSearchForm = new FormGroup({
    facilityOrLocationName: new FormControl('', Validators.required),
    cityOrZip: new FormControl('', Validators.required)
  });


  constructor(private translate: TranslateService, private monitoringService: MonitoringService) {
    this.translate.use(sessionStorage.getItem('locale') || 'en-US'); // Default to 'en-US' if no locale is provided
  }

  ngOnInit() {
    this.translate.get('FINDALOCATION').subscribe((translated: string) => {
      this.title = translated;
    });

    this.translate.get('LOCATION_SEARCH_BY').subscribe((translated: string) => {
      this.searchByText = translated;
    }); 

    this.translate.get('LOCATION_NAME_PLACEHOLDER').subscribe((translated: string) => {
      this.namePlaceholderText = translated;
    });
    this.translate.get('LOCATION_CITYZIP_PLACEHOLDER').subscribe((translated: string) => {
      this.cityZipPlaceholderText = translated;
    });

    this.translate.get('SEARCH').subscribe((translated: string) => {
      this.searchBtnText = translated;
    });

    this.translate.get('QUICK_SEARCH_TITLE').subscribe((translated: string) => {  
      this.quickSearchTitleText = translated;
    });

    this.translate.get('LOCATION_INFORMATION_REQUIRED').subscribe((translated: string) => {  
      this.locationInformationRequiredText = translated;
    });

    // You can perform additional initialization here if needed
    this.locationSearchQuery$.subscribe(query => {
      if (query) {
        this.locationSearchForm.patchValue({
          facilityOrLocationName: query.facilityOrLocationName,
          cityOrZip: query.cityOrZip
        });
      }
    }); 

    this.searchState.locationSortMethod$.subscribe( sortMethod => {
      if (sortMethod) {
        this.sortMethod = sortMethod
      }
    });
    
    this.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });

    // Setup autocomplete filtering for facility/location name
    this.filteredLocationNames$ = this.locationSearchForm.get('facilityOrLocationName')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterLocationNames(value || ''))
    );

    // Setup autocomplete filtering for city/zip
    this.filteredCityOptions$ = this.locationSearchForm.get('cityOrZip')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCityOptions(value || ''))
    );
  }

  private _filterLocationNames(value: string): string[] {
    // Only show suggestions if 3 or more characters have been typed
    if (!value || value.length < 3) {
      return [];
    }

    if (!this.locationSearchTypeAheadOptions || !Array.isArray(this.locationSearchTypeAheadOptions)) {
      console.log('No location type ahead options available');
      return [];
    }

    // Remove special characters and convert to lowercase for comparison
    const normalizeString = (str: string) => str.toLowerCase().replace(/[^a-z0-9\s]/gi, '');
    const filterValue = normalizeString(value);
    
    const filtered = this.locationSearchTypeAheadOptions.filter(option => 
      normalizeString(option).includes(filterValue)
    );
    console.log('Filtered location name results:', filtered);
    return filtered;
  }

  private _filterCityOptions(value: string): string[] {
    // Only show suggestions if 3 or more characters have been typed
    if (!value || value.length < 3) {
      return [];
    }

    if (!this.cityTypeAheadOptions || !Array.isArray(this.cityTypeAheadOptions)) {
      console.log('No city type ahead options available');
      return [];
    }

    // Remove special characters and convert to lowercase for comparison
    const normalizeString = (str: string) => str.toLowerCase().replace(/[^a-z0-9\s]/gi, '');
    const filterValue = normalizeString(value);
    
    const filtered = this.cityTypeAheadOptions.filter(option => 
      normalizeString(option).includes(filterValue)
    );
    console.log('Filtered city options:', filtered);
    return filtered;
  }

  onSubmit() {
    this.searchState.setShowSpinner(true);

    if (this.locationSearchForm.valid) {
      const formValues = this.locationSearchForm.value;

      if (formValues.facilityOrLocationName && formValues.cityOrZip) {

        console.log(formValues);
        var request = new PostGetLocationSearchRequest();
        request.nameOrServiceOrType = formValues.facilityOrLocationName;
        request.cityOrZip = formValues.cityOrZip;

        if(this.currentLocation != null) {
          request.latitude = this.currentLocation?.latitude;
          request.longitude = this.currentLocation?.longitude;
        }

        sessionStorage.setItem('locationSearchMethod', 'freeText');
        console.log('Form Submitted!', formValues, request );

        this.postGetLocationSearch(request);
      }
      else {
        alert('Please fill in all required fields before searching.');
        this.searchState.setShowSpinner(false);
      }
    }
    else {
      alert('Form is invalid. Please check the input fields.');
      console.log('Form is invalid', this.locationSearchForm.errors);
      this.searchState.setShowSpinner(false);
    }
  }

  onQuickSearch(option: any) {
    console.log('Quick Search Clicked!', option);
    this.searchState.setShowSpinner(true);

    var cityOrZip = this.locationSearchForm.get('cityOrZip')?.value ?? null;

    if(!cityOrZip) {
      console.warn('City or Zip is required for Quick Search.');
      this.searchState.setShowSpinner(false);
      return;
    }

    // You can add further processing logic here
    var request = new PostGetLocationSearchRequest();
    request.services = option.serviceList;
    request.types = option.typeList;
    request.cityOrZip = cityOrZip;

    this.searchState.setLocationSearchQuery({
      facilityOrLocationName: '' ,
      cityOrZip: cityOrZip,
    });

    if(this.currentLocation != null) {
      request.latitude = this.currentLocation?.latitude;
      request.longitude = this.currentLocation?.longitude;
    }

    sessionStorage.setItem('locationSearchMethod', 'quickSearch');
    console.log('Submitting Quick Search! ',request );
    this.postGetLocationSearch(request);

  }

  postGetLocationSearch(request: PostGetLocationSearchRequest) {
    this.mcAppGateway.PostGetLocationSearch(request).subscribe({
      next: (response) => {
        console.log('Location Search Response:', response);
        this.locationSearchForm.reset();

        var sortedResponse = this.searchState.sortLocationSearchResults(response || [], this.sortMethod);
        this.searchState.setLocationSearchResults(sortedResponse);
        this.searchState.setLocationSearchFilterToggle(true); 

        // Logic for nameOrServiceOrType typeahead, if exact match(es) found in servicelist or typelist, wipe out request.locationname
        var foundTypeAheadOption = false;
        if (request.nameOrServiceOrType) {
          this.locationSearchFilters?.serviceList?.forEach(service => {
            if (request.nameOrServiceOrType?.toLowerCase() === service.value.toLowerCase()) {   
              service.isSelected = true;
              foundTypeAheadOption = true;
            } 
          });

          this.locationSearchFilters?.typeList?.forEach(type => {
            if (request.nameOrServiceOrType?.toLowerCase() === type.value.toLowerCase()) {   
              type.isSelected = true;
              foundTypeAheadOption = true;
            } 
          });

          if (!foundTypeAheadOption) 
          {
            request.locationName = request.nameOrServiceOrType;
          }
          request.nameOrServiceOrType = "";
        }       

        if (request.services) {
          this.locationSearchFilters?.serviceList?.forEach(service => {
            if (request.services?.toLowerCase().includes(service.value.toLowerCase())) {
              service.isSelected = true;
            }
          });
        }

        if (request.types) {
          this.locationSearchFilters?.typeList?.forEach(type => {
            if (request.types?.toLowerCase().includes(type.value.toLowerCase())) {
              type.isSelected = true;
            }
          });
        }

        if(request.locationName != null && request.locationName != '' && this.locationSearchFilters != null) {
          this.locationSearchFilters.facilityOrLocationName = request.locationName;
        }

        // Update location and distanceInMiles in filters from the request payload
        if (this.locationSearchFilters != null) {
          this.locationSearchFilters.location = request.cityOrZip ?? '';
          this.locationSearchFilters.distanceInMiles = request.distanceInMiles ?? 15;
          this.locationSearchFilters.facilityOrLocationName = request.locationName ?? '';
          this.searchState.setLocationSearchFilters(this.locationSearchFilters);
        }

        // set the search query in state service for display purposes
        this.searchState.setLocationSearchQuery({
          facilityOrLocationName: request.locationName ?? '',
          cityOrZip: request.cityOrZip ?? '',
        });

        this.searchState.setShowSpinner(false);
      },
      error: (error) => {

        // clear previous query & results
        this.searchState.setLocationSearchQuery({facilityOrLocationName: '', cityOrZip: ''});
        this.searchState.clearLocationSearchResults();

        console.error('An error occurred while searching for locations: ', error);
        alert('An error occurred while searching for locations: ' + error.message);

        this.searchState.setShowSpinner(false);
      },
    });
  }
}